import { motion } from 'framer-motion';
import {
    ArrowLeft,
    ArrowRight,
    CalendarDays,
    CheckCircle2,
    CircleSlash,
    Clock3,
    IndianRupee,
    LoaderCircle,
    Link2,
    ShieldCheck,
    Sparkles,
    Tag,
    UserRound,
    XCircle,
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './ExpenseReviewDetails.module.css';

const REVIEW_REASON_CHIPS = [
    'Amount mismatch',
    'Wrong category',
    'Missing invoice attachment',
    'Duplicate entry',
    'Needs more information',
    'Month-end budget exceeded',
    'Incorrect vendor',
    'Resubmit next cycle',
];

const parseActiveStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null;
    } catch {
        return null;
    }
};

const getPendingHours = (createdOn) => {
    if (!createdOn) return 0;
    const parsed = new Date(String(createdOn).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return 0;
    const diffMs = Date.now() - parsed.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
};

const notifyExpenseMutation = () => {
    window.dispatchEvent(new CustomEvent('expenses-dashboard:refresh'));
};

const normalizeExpenseForReview = (payloadExpense) => {
    const items = (Array.isArray(payloadExpense?.items) ? payloadExpense.items : [])
        .filter((item) => Number(item?.isActive) === 1)
        .map((item) => ({
            expenseItemIdentifier: item?.expenseItemIdentifier || '',
            expenseName: item?.expenseName || 'Untitled Item',
            totalAmount: Number(item?.totalAmount || 0),
            amount: Number(item?.amount || 0),
            gstRate: Number(item?.gstRate || 0),
            categoryLabel: item?.category?.categoryLabel || 'Uncategorized',
            subcategoryLabel: item?.subcategory?.subcategoryLabel || 'No subcategory',
            colorHex: item?.category?.colorHex || '#475569',
        }));

    const categorySummary = items.length
        ? `${items.length} items — ${Array.from(new Set(items.map((item) => item.categoryLabel))).join(', ')}`
        : '—';

    return {
        expenseIdentifier: payloadExpense?.expenseIdentifier || '',
        vendorName: payloadExpense?.vendorName || payloadExpense?.vendorIdentifier || '—',
        invoiceNumber: payloadExpense?.vendorInvoiceNumber || '—',
        billDate: payloadExpense?.expenseDate || '',
        expenseType: payloadExpense?.meta?.expenseType || payloadExpense?.expenseType || 'opex',
        loggedByName: payloadExpense?.meta?.createdBy || '—',
        loggedAt: payloadExpense?.meta?.createdOn || '—',
        loggedFromDevice: payloadExpense?.meta?.createdFromDevice || '—',
        pendingHours: getPendingHours(payloadExpense?.meta?.createdOn),
        itemCategoriesSummary: categorySummary,
        notes: payloadExpense?.expenseNotes || '',
        items,
        totalAmount: Number(payloadExpense?.billing?.totalExpense || 0),
        gstAmount: Number(payloadExpense?.billing?.gstAmount || 0),
        paymentStatus: payloadExpense?.payment?.paymentStatus || 'unpaid',
        approvalStatus: payloadExpense?.meta?.approvalStatus || 'pending',
    };
};

const fmtCurrency = (value) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ExpenseReviewDetails = () => {
    const { expenseIdentifier = '' } = useParams();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = parseActiveStore(asCtx?.activeStore);
    const storeIdentifier = activeStore?.storeIdentifier || '';

    const [expense, setExpense] = useState(null);
    const [queueIds, setQueueIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchExpenseDetails = useCallback(async () => {
        if (!storeIdentifier || !expenseIdentifier) {
            setExpense(null);
            setErrorMessage('Missing store or expense identifier.');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await fetch(BASE_URL_API + 'fetchExpenseDetails', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    expenseIdentifier,
                }),
            });
            const result = await response.json();

            if (result?.status === 'success') {
                setExpense(normalizeExpenseForReview(result?.response?.expense || null));
                return;
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            setExpense(null);
            setErrorMessage(result?.message || 'Unable to fetch expense details.');
        } catch {
            setExpense(null);
            setErrorMessage('Unable to connect to server.');
        } finally {
            setIsLoading(false);
        }
    }, [authCtx, expenseIdentifier, storeIdentifier]);

    const fetchQueueIds = useCallback(async () => {
        if (!storeIdentifier) return;

        try {
            const response = await fetch(BASE_URL_API + 'fetchExpensesList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    pageSize: 200,
                    sort: { sortBy: 'createdOn', sortOrder: 'DESC' },
                    filters: { approvalStatus: 'pending' },
                }),
            });
            const result = await response.json();

            if (result?.status === 'success') {
                const ids = (Array.isArray(result?.response?.expenses) ? result.response.expenses : [])
                    .map((item) => item?.expenseIdentifier)
                    .filter(Boolean);
                setQueueIds(ids);
                return;
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
            }
        } catch {
            setQueueIds([]);
        }
    }, [authCtx, storeIdentifier]);

    useEffect(() => {
        fetchExpenseDetails();
        fetchQueueIds();
    }, [fetchExpenseDetails, fetchQueueIds]);

    const activeIndex = useMemo(() => queueIds.findIndex((id) => id === expenseIdentifier), [queueIds, expenseIdentifier]);
    const prevExpenseIdentifier = activeIndex > 0 ? queueIds[activeIndex - 1] : null;
    const nextExpenseIdentifier = activeIndex >= 0 && activeIndex < queueIds.length - 1 ? queueIds[activeIndex + 1] : null;

    const prevExpense = prevExpenseIdentifier ? { expenseIdentifier: prevExpenseIdentifier } : null;
    const nextExpense = nextExpenseIdentifier ? { expenseIdentifier: nextExpenseIdentifier } : null;

    const getAuthIdentifier = () => {
        try {
            return authCtx?.authIdentifier || '';
        } catch {
            return '';
        }
    };

    const approve = async () => {
        const authIdentifier = getAuthIdentifier();
        if (!authIdentifier) {
            toast.error('Session error. Please log in again.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsProcessing(true);
        try {
            const response = await fetch(BASE_URL_API + 'reviewExpense', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    authIdentifier,
                    expenseIdentifier,
                    decision: 'approved',
                }),
            });
            const result = await response.json();

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            if (result?.status === 'success') {
                const res = Array.isArray(result?.response?.results) ? result.response.results[0] : null;
                const amountStr = res?.totalExpense ? ` ₹${Number(res.totalExpense).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '';
                toast.success(`Expense approved.${amountStr}`, { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                notifyExpenseMutation();
                navigate('/expenses/review');
                return;
            }

            if (result?.response?.currentStatus) {
                toast.info(`Expense is already ${result.response.currentStatus}. Refreshing...`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                fetchExpenseDetails();
                return;
            }

            toast.error(result?.message || 'Failed to approve expense.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
        } finally {
            setIsProcessing(false);
        }
    };

    const reject = async () => {
        const reason = rejectReason.trim();
        if (!reason) {
            toast.error('Please provide rejection reason.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        const authIdentifier = getAuthIdentifier();
        if (!authIdentifier) {
            toast.error('Session error. Please log in again.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsProcessing(true);
        try {
            const response = await fetch(BASE_URL_API + 'reviewExpense', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    authIdentifier,
                    expenseIdentifier,
                    decision: 'rejected',
                    reason,
                }),
            });
            const result = await response.json();

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            if (result?.status === 'success') {
                toast.success('Expense rejected.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
                setIsRejectModalOpen(false);
                notifyExpenseMutation();
                navigate('/expenses/review');
                return;
            }

            if (result?.response?.currentStatus) {
                toast.info(`Expense is already ${result.response.currentStatus}. Refreshing...`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                setIsRejectModalOpen(false);
                fetchExpenseDetails();
                return;
            }

            toast.error(result?.message || 'Failed to reject expense.', { position: 'top-center', hideProgressBar: true, autoClose: 2400 });
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.notFoundCard}>
                    <LoaderCircle size={22} className={styles.spin} />
                    <h3>Loading review details...</h3>
                </div>
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className={styles.page}>
                <div className={styles.notFoundCard}>
                    <CircleSlash size={22} />
                    <h3>Unable to load review details</h3>
                    <p>{errorMessage}</p>
                    <button type="button" className={styles.backBtn} onClick={fetchExpenseDetails}>Retry</button>
                </div>
            </div>
        );
    }

    if (!expense) {
        return (
            <div className={styles.page}>
                <div className={styles.notFoundCard}>
                    <CircleSlash size={22} />
                    <h3>Review record not found</h3>
                    <p>This expense might already be approved/rejected, or no longer in queue.</p>
                    <Link to="/expenses/review" className={styles.backBtn}>Back to Queue</Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <motion.section className={styles.hero} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
                <div className={styles.heroTop}>
                    <Link to="/expenses/review" className={styles.backLink}><ArrowLeft size={13} /> Back to Queue</Link>
                    <span className={styles.pill}><Sparkles size={13} /> Read-only Review Mode</span>
                </div>

                <h1>Expense Review</h1>
                <p>Verify details, approve quickly, or reject with a reason. No edit/delete actions on this screen.</p>

                <div className={styles.heroMeta}>
                    <span className={styles.metaChip}>ID: {expense.expenseIdentifier}</span>
                    <span className={styles.metaChip}><Clock3 size={12} /> Pending {expense.pendingHours}h</span>
                    <span className={styles.metaChip}><UserRound size={12} /> {expense.loggedByName}</span>
                </div>
            </motion.section>

            <div className={styles.layout}>
                <div className={styles.mainCol}>
                    <section className={styles.card}>
                        <div className={styles.cardHead}><CalendarDays size={14} /> Bill Snapshot</div>
                        <div className={styles.grid2}>
                            <div><span>Vendor</span><strong>{expense.vendorName}</strong></div>
                            <div><span>Invoice</span><strong>{expense.invoiceNumber || '—'}</strong></div>
                            <div><span>Bill Date</span><strong>{formatDate(expense.billDate)}</strong></div>
                            <div><span>Expense Type</span><strong>{String(expense.expenseType || '—').toUpperCase()}</strong></div>
                            <div><span>Logged By</span><strong>{expense.loggedByName}</strong></div>
                            <div><span>Logged At</span><strong>{expense.loggedAt}</strong></div>
                            <div><span>Device</span><strong>{expense.loggedFromDevice || '—'}</strong></div>
                            <div><span>Category Summary</span><strong>{expense.itemCategoriesSummary}</strong></div>
                        </div>
                        {expense.notes && <p className={styles.notes}>{expense.notes}</p>}
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHead}><Tag size={14} /> Active Items ({expense.items.length})</div>
                        <div className={styles.items}>
                            {expense.items.map((item) => (
                                <div className={styles.item} key={item.expenseItemIdentifier}>
                                    <div className={styles.itemTop}>
                                        <h4>{item.expenseName}</h4>
                                        <span>₹{fmtCurrency(item.totalAmount)}</span>
                                    </div>
                                    <div className={styles.itemChips}>
                                        <span className={styles.categoryChip} style={{ borderColor: `${item.colorHex}66`, backgroundColor: `${item.colorHex}14`, color: item.colorHex || '#475569' }}>{item.categoryLabel}</span>
                                        <span>{item.subcategoryLabel || 'No subcategory'}</span>
                                        <span>Net ₹{fmtCurrency(item.amount)}</span>
                                        <span>GST {item.gstRate}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className={styles.sideCol}>
                    <section className={styles.card}>
                        <div className={styles.cardHead}><IndianRupee size={14} /> Financial Summary</div>
                        <div className={styles.statList}>
                            <div><span>Total Amount</span><strong>₹{fmtCurrency(expense.totalAmount)}</strong></div>
                            <div><span>GST Amount</span><strong>₹{fmtCurrency(expense.gstAmount)}</strong></div>
                            <div><span>Payment Status</span><strong>{expense.paymentStatus}</strong></div>
                            <div><span>Approval Status</span><strong>{expense.approvalStatus}</strong></div>
                        </div>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHead}><ShieldCheck size={14} /> Review Actions</div>
                        <div className={styles.actionsCard}>
                            <button type="button" className={styles.rejectBtn} onClick={() => setIsRejectModalOpen(true)} disabled={isProcessing || isLoading}>
                                <XCircle size={13} /> Reject
                            </button>
                            <button type="button" className={styles.approveBtn} onClick={approve} disabled={isProcessing || isLoading}>
                                <CheckCircle2 size={13} /> {isProcessing ? 'Processing...' : 'Approve'}
                            </button>
                            <p className={styles.actionsHint}>Reject requires reason. Approve supports quick decision for routine bills.</p>
                        </div>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHead}><Link2 size={14} /> Queue Navigation</div>
                        <div className={styles.navRow}>
                            <button type="button" className={styles.navBtn} onClick={() => prevExpense && navigate(`/expenses/review/${prevExpense.expenseIdentifier}`)} disabled={!prevExpense || isLoading}>
                                <ArrowLeft size={13} /> Prev
                            </button>
                            <button type="button" className={styles.navBtn} onClick={() => nextExpense && navigate(`/expenses/review/${nextExpense.expenseIdentifier}`)} disabled={!nextExpense || isLoading}>
                                Next <ArrowRight size={13} />
                            </button>
                        </div>
                    </section>
                </div>
            </div>

            {isRejectModalOpen && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalCard}>
                        <h3>Reject Expense</h3>
                        <p>Select a quick reason or add a custom note.</p>
                        <div className={styles.reasonChips}>
                            {REVIEW_REASON_CHIPS.map((reason) => (
                                <button
                                    type="button"
                                    key={reason}
                                    className={`${styles.reasonChip} ${rejectReason === reason ? styles.reasonChipActive : ''}`}
                                    onClick={() => setRejectReason(reason)}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>
                        <textarea
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Type rejection reason"
                        />
                        <div className={styles.modalActions}>
                            <button type="button" className={styles.cancelBtn} onClick={() => setIsRejectModalOpen(false)} disabled={isProcessing}>Cancel</button>
                            <button type="button" className={styles.confirmRejectBtn} onClick={reject} disabled={isProcessing}>Reject Bill</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseReviewDetails;
